import { useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  faCircleExclamation,
  faCircleInfo,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faBuildingUser,
  faClock,
  faHeadset,
  faIdCard,
  faLaptopMobile,
  faPlus,
  faRotateLeft,
} from '@fortawesome/pro-solid-svg-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { observer } from 'mobx-react'

import { Alert } from 'Components/_theme/Alert/Alert'
import { Card } from 'Components/_theme/Card/Card'
import { CardBody } from 'Components/_theme/CardBody/CardBody'
import { CardHeader } from 'Components/_theme/CardHeader/CardHeader'
import { Heading } from 'Components/_theme/Heading/Heading'
import { LinkButton } from 'Components/_theme/LinkButton/LinkButton'
import { PrimaryButton } from 'Components/_theme/PrimaryButton/PrimaryButton'
import { TertiaryButton } from 'Components/_theme/TertiaryButton/TertiaryButton'
import { CountrySelect } from 'Components/CountrySelect/CountrySelect'
import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'
import { Form } from 'Components/Form/Form'
import { Icon } from 'Components/Icon/Icon'

import { RETURN_LABEL_NAME_MAX_LENGTH } from 'Constants/returnLabel'

import { DeviceIdentifierBlock } from './DeviceIdentifierBlock'
import {
  DEFAULT_DEVICE,
  EVERPHONE_SUPPORT_URL,
  MAX_DEVICES,
} from './PublicRequestReturnLabel.constants'
import { getPublicRequestReturnLabelFormSchema } from './PublicRequestReturnLabel.schema'
import type {
  PublicRequestReturnLabelFormProps,
  PublicRequestReturnLabelFormValues,
} from './PublicRequestReturnLabel.types'
import { countDeviceIdentifiers } from './PublicRequestReturnLabel.utils'

// The requester name and company name are capped: companyName because it lands
// on the carrier payload as sender_address.company and DHL rejects it above this
// length, contactName by product decision. Both surface the limit as a hint
// under the input so the ceiling is visible before it is hit, and the same
// values fill in the max-length error when a paste overshoots it.
//
// senderName is deliberately absent - it is free-form and stays uncapped.
const nameFieldFeedbackValues = {
  amount: RETURN_LABEL_NAME_MAX_LENGTH,
}

const nameFieldInputProps = {
  maxLength: RETURN_LABEL_NAME_MAX_LENGTH,
}

const NAME_MAX_LENGTH_HINT = 'PublicRequestReturnLabel_nameMaxLengthHint'

export const PublicRequestReturnLabelForm = observer(
  (props: PublicRequestReturnLabelFormProps) => {
    const {
      hasSubmitError,
      initialDeviceIdentifier = '',
      isSubmitting,
      onSubmit,
      showCustomerSupportCard,
      showLabelComingSoon,
      showDeviceSection = true,
      showTokenModeBanner = false,
      showUsePrefilledButton = false,
      onUseManualDevices,
      onUsePrefilledDevices,
    } = props
    const intl = useIntl()

    const deviceKeyCounter = useRef(0)
    const [deviceKeys, setDeviceKeys] = useState<string[]>([
      String(deviceKeyCounter.current),
    ])

    const resolver = useMemo(
      () =>
        zodResolver(getPublicRequestReturnLabelFormSchema(showDeviceSection)),
      [showDeviceSection],
    )

    const formMethods = useForm<PublicRequestReturnLabelFormValues>({
      defaultValues: {
        city: '',
        companyName: '',
        contactEmail: '',
        contactName: '',
        country: null,
        devices: [{ ...DEFAULT_DEVICE, value: initialDeviceIdentifier }],
        senderEmail: '',
        senderName: '',
        senderPhone: '',
        street: '',
        streetNumber: '',
        zipcode: '',
      },
      resolver,
    })
    const { handleSubmit, setValue, control, clearErrors } = formMethods

    const devices = useWatch({ control, name: 'devices' })
    const country = useWatch({ control, name: 'country' })
    const isNonGerman = !!country?.value && country.value !== 'DE'

    useEffect(() => {
      if (!isNonGerman) {
        setValue('senderEmail', '')
        setValue('senderPhone', '')
        clearErrors(['senderEmail', 'senderPhone'])
      }
    }, [isNonGerman, setValue, clearErrors])

    const totalIdentifiers = countDeviceIdentifiers(devices || [])
    const canAddDevice =
      totalIdentifiers < MAX_DEVICES && (devices?.length || 0) < MAX_DEVICES
    // Native `<fieldset disabled>` propagates to inputs / radios / buttons but
    // not to react-select (div-based). The country field needs `isDisabled` on
    // its selectProps to lock alongside the rest of the form.
    const isFormLocked =
      isSubmitting || showCustomerSupportCard || showLabelComingSoon

    const handleAddDevice = () => {
      if (!canAddDevice) {
        return
      }

      const currentDevices = devices || []

      clearErrors('devices')
      setValue('devices', [...currentDevices, { ...DEFAULT_DEVICE }])
      deviceKeyCounter.current += 1
      setDeviceKeys(prev => [...prev, String(deviceKeyCounter.current)])
    }

    const handleRemoveDevice = (index: number) => {
      const currentDevices = devices || []

      if (currentDevices.length <= 1) {
        return
      }

      clearErrors('devices')
      setValue(
        'devices',
        currentDevices.filter((_, i) => i !== index),
      )
      setDeviceKeys(prev => prev.filter((_, i) => i !== index))
    }

    const handleUsePrefilledDevices = () => {
      clearErrors('devices')
      deviceKeyCounter.current += 1
      setValue('devices', [{ ...DEFAULT_DEVICE }])
      setDeviceKeys([String(deviceKeyCounter.current)])
      onUsePrefilledDevices?.()
    }

    return (
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <fieldset className="border-0 p-0 m-0" disabled={isFormLocked}>
            {showTokenModeBanner && (
              <Alert
                className="mb-4 mb-lg-6"
                iconLeft={faCircleInfo}
                title={intl.formatMessage({
                  id: 'PublicRequestReturnLabel_tokenMode_title',
                })}
                variant="info"
              >
                <p>
                  <FormattedMessage id="PublicRequestReturnLabel_tokenMode_description" />
                </p>
                <div className="d-flex gap-3 mt-3">
                  <TertiaryButton
                    onClick={onUseManualDevices}
                    size="small"
                    type="button"
                  >
                    <FormattedMessage id="PublicRequestReturnLabel_tokenMode_useManual" />
                  </TertiaryButton>
                </div>
              </Alert>
            )}

            {showDeviceSection && (
              <Card className="mb-4 mb-lg-6">
                <CardHeader
                  icon={faLaptopMobile}
                  subtitle={
                    <span className="text-muted">
                      <FormattedMessage
                        id="PublicRequestReturnLabel_deviceIdentifier_shipmentInfo"
                        values={{
                          c: chunks => (
                            <span
                              className={
                                totalIdentifiers > MAX_DEVICES
                                  ? 'text-danger'
                                  : undefined
                              }
                            >
                              {chunks}
                            </span>
                          ),
                          count: totalIdentifiers,
                        }}
                      />
                    </span>
                  }
                >
                  <FormattedMessage id="PublicRequestReturnLabel_deviceInfo_heading" />
                </CardHeader>
                <CardBody>
                  <div className="d-flex flex-column gap-3">
                    {(devices || []).map((_, index) => (
                      <DeviceIdentifierBlock
                        key={deviceKeys[index]}
                        index={index}
                        onRemove={() => handleRemoveDevice(index)}
                        showRemove={(devices?.length || 0) > 1}
                      />
                    ))}
                  </div>

                  <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mt-4">
                    <LinkButton
                      disabled={!canAddDevice}
                      iconLeft={faPlus}
                      onClick={handleAddDevice}
                    >
                      <FormattedMessage id="PublicRequestReturnLabel_deviceIdentifier_addAnother" />
                    </LinkButton>

                    {showUsePrefilledButton && (
                      <LinkButton
                        iconLeft={faRotateLeft}
                        onClick={handleUsePrefilledDevices}
                        size="small"
                        variant="black"
                      >
                        <FormattedMessage id="PublicRequestReturnLabel_tokenMode_usePrefilled" />
                      </LinkButton>
                    )}
                  </div>
                </CardBody>
              </Card>
            )}

            <Card className="mb-4 mb-lg-6">
              <CardHeader
                icon={faBuildingUser}
                subtitle={
                  <span className="text-muted">
                    <FormattedMessage id="PublicRequestReturnLabel_contact_subtitle" />
                  </span>
                }
              >
                <FormattedMessage id="PublicRequestReturnLabel_contact_heading" />
              </CardHeader>
              <CardBody>
                <Field
                  autoComplete="name"
                  feedbackText={NAME_MAX_LENGTH_HINT}
                  feedbackTranslationValues={nameFieldFeedbackValues}
                  inputProps={nameFieldInputProps}
                  label="PublicRequestReturnLabel_contact_fullName"
                  markAsRequired
                  name="contactName"
                  placeholder={intl.formatMessage({
                    id: 'PublicRequestReturnLabel_contact_fullNamePlaceholder',
                  })}
                  type={FieldTypeEnum.text}
                />
                <Field
                  autoComplete="email"
                  containerClassName="mt-6"
                  label="PublicRequestReturnLabel_contact_email"
                  markAsRequired
                  name="contactEmail"
                  placeholder={intl.formatMessage({
                    id: 'PublicRequestReturnLabel_contact_emailPlaceholder',
                  })}
                  type={FieldTypeEnum.email}
                />
                <Field
                  autoComplete="organization"
                  containerClassName="mt-6"
                  feedbackText={NAME_MAX_LENGTH_HINT}
                  feedbackTranslationValues={nameFieldFeedbackValues}
                  inputProps={nameFieldInputProps}
                  label="PublicRequestReturnLabel_contact_companyName"
                  markAsRequired
                  name="companyName"
                  placeholder={intl.formatMessage({
                    id: 'PublicRequestReturnLabel_contact_companyNamePlaceholder',
                  })}
                  type={FieldTypeEnum.text}
                />
              </CardBody>
            </Card>

            <Card className="mb-4 mb-lg-6">
              <CardHeader
                icon={faIdCard}
                subtitle={
                  <span className="text-muted">
                    <FormattedMessage id="PublicRequestReturnLabel_sender_subtitle" />
                  </span>
                }
              >
                <FormattedMessage id="PublicRequestReturnLabel_sender_heading" />
              </CardHeader>
              <CardBody>
                <Field
                  autoComplete="name"
                  label="PublicRequestReturnLabel_sender_fullName"
                  markAsRequired
                  name="senderName"
                  placeholder={intl.formatMessage({
                    id: 'PublicRequestReturnLabel_sender_fullNamePlaceholder',
                  })}
                  type={FieldTypeEnum.text}
                />
                <Row className="mt-6">
                  <Col md={8} xs={12}>
                    <Field
                      autoComplete="address-line1"
                      label="PublicRequestReturnLabel_sender_street"
                      markAsRequired
                      name="street"
                      placeholder={intl.formatMessage({
                        id: 'PublicRequestReturnLabel_sender_streetPlaceholder',
                      })}
                      type={FieldTypeEnum.text}
                    />
                  </Col>
                  <Col className="mt-3 mt-md-0" md={4} xs={12}>
                    <Field
                      autoComplete="off"
                      label="PublicRequestReturnLabel_sender_streetNumber"
                      markAsRequired
                      name="streetNumber"
                      placeholder={intl.formatMessage({
                        id: 'PublicRequestReturnLabel_sender_streetNumberPlaceholder',
                      })}
                      type={FieldTypeEnum.text}
                    />
                  </Col>
                </Row>

                <div className="d-flex flex-column flex-md-row gap-3 mt-6">
                  <Field
                    autoComplete="postal-code"
                    containerClassName="flex-grow-1"
                    label="PublicRequestReturnLabel_sender_zipCode"
                    markAsRequired
                    name="zipcode"
                    placeholder={intl.formatMessage({
                      id: 'PublicRequestReturnLabel_sender_zipCodePlaceholder',
                    })}
                    type={FieldTypeEnum.text}
                  />
                  <Field
                    autoComplete="address-level2"
                    containerClassName="flex-grow-1"
                    label="PublicRequestReturnLabel_sender_city"
                    markAsRequired
                    name="city"
                    placeholder={intl.formatMessage({
                      id: 'PublicRequestReturnLabel_sender_cityPlaceholder',
                    })}
                    type={FieldTypeEnum.text}
                  />
                </div>

                <CountrySelect
                  containerClassName="mt-6"
                  disabled={isFormLocked}
                  isClearable={false}
                  isShippingLabelList
                  label="PublicRequestReturnLabel_sender_country"
                  markAsRequired
                  name="country"
                  placeholder={intl.formatMessage({
                    id: 'PublicRequestReturnLabel_sender_countryPlaceholder',
                  })}
                  topCountryCode={props.topCountryCode}
                />

                {isNonGerman && (
                  <>
                    <Field
                      autoComplete="email"
                      containerClassName="mt-6"
                      label="PublicRequestReturnLabel_sender_email"
                      name="senderEmail"
                      type={FieldTypeEnum.email}
                    />
                    <Field
                      containerClassName="mt-6"
                      label="PublicRequestReturnLabel_sender_phone"
                      name="senderPhone"
                      phoneInputProps={{
                        country: country?.value?.toLowerCase() || 'de',
                        enableLongNumbers: true,
                        preferredCountries: [
                          country?.value?.toLowerCase() || 'de',
                        ],
                      }}
                      type={FieldTypeEnum.phoneNumber}
                    />
                  </>
                )}
              </CardBody>
            </Card>

            {hasSubmitError && (
              <Alert
                className="mb-4"
                iconLeft={faCircleExclamation}
                variant="danger"
              >
                <FormattedMessage id="PublicRequestReturnLabel_submitError" />
              </Alert>
            )}

            {showCustomerSupportCard && (
              <div
                aria-live="polite"
                className="PublicRequestReturnLabel-customerSupportSection p-6 mb-4 d-flex align-items-start gap-4"
                role="status"
              >
                <div className="PublicRequestReturnLabel-customerSupportIconBadge d-flex flex-shrink-0 align-items-center justify-content-center">
                  <Icon icon={faHeadset} size={24} />
                </div>
                <div className="d-flex flex-column align-items-start gap-3">
                  <Heading as={5} className="m-0" size={2}>
                    <FormattedMessage id="PublicRequestReturnLabel_customerSupport_heading" />
                  </Heading>
                  <p className="PublicRequestReturnLabel-customerSupportBody m-0">
                    <FormattedMessage id="PublicRequestReturnLabel_customerSupport_body" />
                  </p>
                  <a
                    className="Button Button-medium PrimaryButton"
                    href={EVERPHONE_SUPPORT_URL}
                  >
                    <span>
                      <FormattedMessage id="PublicRequestReturnLabel_customerSupport_button" />
                    </span>
                  </a>
                </div>
              </div>
            )}

            {showLabelComingSoon && (
              <div
                aria-live="polite"
                className="PublicRequestReturnLabel-customerSupportSection p-6 mb-4 d-flex align-items-start gap-4"
                role="status"
              >
                <div className="PublicRequestReturnLabel-customerSupportIconBadge d-flex flex-shrink-0 align-items-center justify-content-center">
                  <Icon icon={faClock} size={24} />
                </div>
                <div className="d-flex flex-column align-items-start gap-3">
                  <Heading as={5} className="m-0" size={2}>
                    <FormattedMessage id="PublicRequestReturnLabel_labelComingSoon_heading" />
                  </Heading>
                  <p className="PublicRequestReturnLabel-customerSupportBody m-0">
                    <FormattedMessage id="PublicRequestReturnLabel_labelComingSoon_body" />
                  </p>
                </div>
              </div>
            )}

            <PrimaryButton
              className="w-100 mt-4"
              disabled={isFormLocked || totalIdentifiers > MAX_DEVICES}
              isLoading={isSubmitting}
              size="large"
              type="submit"
            >
              <FormattedMessage id="PublicRequestReturnLabel_submit" />
            </PrimaryButton>
          </fieldset>
        </Form>
      </FormProvider>
    )
  },
)
